import { AppBar, Autocomplete, Chip, CircularProgress, Skeleton, Tab } from "@mui/material";
import Tabs from "@/components/atoms/ZoomAwareTabs";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React, { ReactElement, useEffect, useMemo, useState } from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import {
  DESTINATION_WORKFLOW_STEP_NAME_OPTIONS,
  EXCEPTION_SOURCE_WORKFLOW_STEP_NAME_OPTIONS,
  ALLOW_ATTENDANT_TRANSFER_OPTIONS,
  STEP_TYPE_OPTIONS,
  WORKFLOW_ELEMENT_ID_OPTIONS,
  WORKFLOW_ENGINE_ID_OPTIONS,
  getMenuOptionTargetSteps,
} from "@/components/screens/WorkflowFormPage/constants";
import useGetGlobalVariables from "@/hooks/requests/globalVariable/use-get-global-variables";
import useGetPromptAI from "@/hooks/requests/promptAI/use-get-prompt-ai";
import useGetWorkflowEngineBackoffice from "@/hooks/requests/workflowEngine/use-get-workflows-engine-backoffice";
import useDialog from "@/hooks/use-dialog";
import useTabs from "@/hooks/use-tabs";
import { IPromptAI } from "@/interfaces/prompt-ai.interface";
import IWorkflowEngine from "@/interfaces/workflow-engine.interfaces";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { IWorkflowStep, IWorkflowStepInput } from "@/interfaces/workflow-step.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";
import { FilterType } from "@/types/filter.types";
import { SelectData } from "@/types/form.types";

import { buildWorkflowStepFormInitialData, buildWorkflowStepFormSchema } from "../../form-data";
import ContentPreviewBubble from "./components/ContentPreviewBubble";
import StepNavigateShortcutButton from "./components/StepNavigateShortcutButton";
import { STEP_FORM_TABS_ITEMS } from "./constants";
import { WorkflowStepFormTabs } from "./types";

type Props = {
  messages: WorkflowEditDialogMessages;
  submitForm: (_formData: IWorkflowStepInput) => void;
  isLoading: boolean;
  loadingIcon: ReactElement<unknown, string>;
  step: IWorkflowStep | null;
  workflow?: IWorkflow;
  isEditing?: boolean;
  isAdmin?: boolean;
  onNavigateToStep?: (targetStep: IWorkflowStep) => void;
};

type PromptAIFieldName = "promptAIInputId" | "promptAIExceptionId";
const PROMPT_AI_BATCH_SIZE = 100;

type PromptAIAutocompleteFieldProps = {
  disabled: boolean;
  fieldName: PromptAIFieldName;
  label: string;
  value: number | null | undefined;
  setFieldValue: (field: string, value: number | null) => void | Promise<unknown>;
  onSelectedOptionChange?: (option: IPromptAI | null) => void;
};

type StepNameAutocompleteProps = {
  label: string;
  options: SelectData<string>[];
  value: string;
  error?: string;
  disabled?: boolean;
  shortcutAriaLabel: string;
  onChange: (value: string) => void;
  onNavigate?: () => void;
  canNavigate?: boolean;
};

const DEFAULT_PROMPT_AI_FILTER = [
  {
    property: "Description" as const,
    filterType: FilterType.Contains,
    value: "",
  },
];

const getPromptAIDescription = (promptAI: IPromptAI) =>
  promptAI.description?.trim() || promptAI.Description?.trim() || "Sem descricao";

const getPromptAIContent = (promptAI: IPromptAI | null | undefined) =>
  promptAI?.content?.trim() || promptAI?.Content?.trim() || "";

const buildPromptAIPlaceholderOption = (id: number): IPromptAI => ({
  id,
  friendlyName: `Prompt ${id}`,
  description: `Prompt ${id}`,
  content: "",
});

const getEnginePreview = (engine: IWorkflowEngine | undefined) =>
  engine?.description?.trim() || engine?.name?.trim() || "";

const parseParameters = (returnString: string): string[] => {
  const regex = /(\w+)\s*:/g;
  let match;
  const params: string[] = [];
  while ((match = regex.exec(returnString)) !== null) {
    if (match[1] !== "Sucess" && match[1] !== "Error") {
      params.push(match[1]);
    }
  }
  return params;
};

const getEngineParams = (workflowEngineId: number, engines: IWorkflowEngine[]): string[] => {
  const engine = engines.find((e) => e.id === workflowEngineId);
  return engine ? parseParameters(engine.return) : [];
};

const PromptAIAutocompleteField = ({
  disabled,
  fieldName,
  label,
  value,
  setFieldValue,
  onSelectedOptionChange,
}: PromptAIAutocompleteFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [resolvedOption, setResolvedOption] = useState<IPromptAI | null>(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchTerm(inputValue.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [inputValue]);

  const filters = useMemo(
    () => [
      {
        property: "Description" as const,
        filterType: FilterType.Contains,
        value: searchTerm,
      },
    ],
    [searchTerm]
  );

  const selectedByIdFilters = useMemo(
    () =>
      value == null
        ? DEFAULT_PROMPT_AI_FILTER
        : [
            {
              property: "Id" as const,
              filterType: FilterType.Equals,
              value: String(value),
            },
          ],
    [value]
  );

  const { data, isLoading } = useGetPromptAI({
    pageNumber: 1,
    pageSize: PROMPT_AI_BATCH_SIZE,
    filters: searchTerm ? filters : DEFAULT_PROMPT_AI_FILTER,
    fetchAll: true,
  });

  const options = data?.items ?? [];
  const optionFromList = useMemo(
    () => (value == null ? null : options.find((option) => option.id === value) ?? null),
    [options, value]
  );

  const needsSelectedByIdFetch =
    value != null && !optionFromList && resolvedOption?.id !== value;

  const { data: selectedByIdData, isLoading: isLoadingSelectedById } = useGetPromptAI({
    pageNumber: 1,
    pageSize: 1,
    filters: selectedByIdFilters,
    fetchAll: false,
    isPaused: !needsSelectedByIdFetch,
  });

  const optionFromById = useMemo(() => {
    if (value == null) return null;
    return selectedByIdData?.items?.find((option) => option.id === value) ?? null;
  }, [selectedByIdData?.items, value]);

  useEffect(() => {
    if (value == null) {
      setResolvedOption(null);
      return;
    }

    const nextOption = optionFromList ?? optionFromById;
    if (!nextOption) return;

    setResolvedOption((current) =>
      current?.id === nextOption.id &&
      getPromptAIContent(current) === getPromptAIContent(nextOption) &&
      getPromptAIDescription(current) === getPromptAIDescription(nextOption)
        ? current
        : nextOption
    );
  }, [value, optionFromList, optionFromById]);

  const selectedOption = useMemo(() => {
    if (value == null) return null;
    if (optionFromList) return optionFromList;
    if (resolvedOption?.id === value) return resolvedOption;
    if (optionFromById) return optionFromById;
    return buildPromptAIPlaceholderOption(value);
  }, [value, optionFromList, resolvedOption, optionFromById]);

  const autocompleteOptions = useMemo(() => {
    if (!selectedOption) return options;

    return options.some((option) => option.id === selectedOption.id)
      ? options
      : [selectedOption, ...options];
  }, [options, selectedOption]);

  useEffect(() => {
    if (isOpen) return;

    setInputValue(selectedOption ? getPromptAIDescription(selectedOption) : "");
  }, [isOpen, selectedOption]);

  useEffect(() => {
    if (value == null) {
      onSelectedOptionChange?.(null);
      return;
    }

    // Avoid wiping the preview with an empty placeholder while the list/by-id fetch is in flight.
    const previewOption =
      optionFromList ??
      (resolvedOption?.id === value ? resolvedOption : null) ??
      optionFromById;

    if (previewOption) {
      onSelectedOptionChange?.(previewOption);
    }
  }, [
    value,
    optionFromList,
    optionFromById,
    resolvedOption,
    onSelectedOptionChange,
  ]);

  const isResolvingSelected =
    value != null && !getPromptAIContent(selectedOption) && (isLoading || isLoadingSelectedById);

  return (
    <Autocomplete
      fullWidth
      open={isOpen}
      openOnFocus
      options={autocompleteOptions}
      filterOptions={(items) => items}
      value={selectedOption}
      inputValue={inputValue}
      loading={isLoading || isResolvingSelected}
      disabled={disabled}
      noOptionsText="Nenhum prompt encontrado."
      loadingText="Buscando prompts..."
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(option) => getPromptAIDescription(option)}
      onOpen={() => {
        setSearchTerm("");
        setIsOpen(true);
      }}
      onClose={() => setIsOpen(false)}
      onChange={(_, newValue) => {
        void setFieldValue(fieldName, newValue?.id ?? null);
        setResolvedOption(newValue);
        onSelectedOptionChange?.(newValue);
        setInputValue(newValue ? getPromptAIDescription(newValue) : "");
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "reset") {
          setInputValue(selectedOption ? getPromptAIDescription(selectedOption) : "");
          return;
        }

        if (reason === "clear") {
          void setFieldValue(fieldName, null);
          setResolvedOption(null);
          onSelectedOptionChange?.(null);
          setInputValue("");
          return;
        }

        setInputValue(newInputValue);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <MDTypography variant="button" color="text">
            {getPromptAIDescription(option)}
          </MDTypography>
        </li>
      )}
      renderInput={(params) => (
        <MDInput
          {...params}
          label={label}
          variant="outlined"
          disabledIcon
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading || isResolvingSelected ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const StepNameAutocomplete = ({
  label,
  options,
  value,
  error,
  disabled,
  shortcutAriaLabel,
  onChange,
  onNavigate,
  canNavigate = false,
}: StepNameAutocompleteProps) => (
  <Grid container spacing={1} alignItems="center">
    <Grid size="grow">
      <Autocomplete
        fullWidth
        options={options}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        value={options.find((opt) => opt.value === (value || "")) || null}
        disabled={disabled}
        onChange={(_, newValue) => onChange(newValue?.value ?? "")}
        renderInput={(params) => (
          <MDInput
            label={label}
            {...params}
            variant="outlined"
            error={!!error}
            helperText={error}
            disabled={disabled}
            disabledIcon
          />
        )}
      />
    </Grid>
    {onNavigate && (
      <Grid size="auto">
        <StepNavigateShortcutButton
          ariaLabel={shortcutAriaLabel}
          disabled={!canNavigate}
          onClick={onNavigate}
        />
      </Grid>
    )}
  </Grid>
);

const WorkflowStepForm = ({
  messages,
  submitForm,
  isLoading,
  step,
  workflow,
  isEditing = false,
  isAdmin = false,
  onNavigateToStep,
}: Props) => {
  const workflowEng = useGetWorkflowEngineBackoffice();
  const { data: engines, isLoading: isLoadingEngParams } = workflowEng;
  const { data: globalVariables, isLoading: isLoadingGlobalVariables } = useGetGlobalVariables();

  const [selectedPromptInput, setSelectedPromptInput] = useState<IPromptAI | null>(null);
  const [selectedPromptException, setSelectedPromptException] = useState<IPromptAI | null>(null);

  useEffect(() => {
    setSelectedPromptInput(null);
    setSelectedPromptException(null);
  }, [step?.id]);

  const workflowSteps = workflow?.workflowSteps ?? [];
  const initialValues = buildWorkflowStepFormInitialData(step, workflowSteps);
  const validationSchema = buildWorkflowStepFormSchema(messages, isEditing);

  const stepTypeOptions = STEP_TYPE_OPTIONS(messages);
  const allowAttendantTransferOptions = ALLOW_ATTENDANT_TRANSFER_OPTIONS(messages);
  const workflowElementIdOptions = WORKFLOW_ELEMENT_ID_OPTIONS();
  const workflowEngineIdOptions = WORKFLOW_ENGINE_ID_OPTIONS(engines?.items, messages);
  const exceptionSourceOptions = EXCEPTION_SOURCE_WORKFLOW_STEP_NAME_OPTIONS(
    workflowSteps,
    messages
  );
  const gotoStepOptions = DESTINATION_WORKFLOW_STEP_NAME_OPTIONS(workflowSteps, messages);

  const TABS = STEP_FORM_TABS_ITEMS(messages);
  const tabs = useTabs<WorkflowStepFormTabs>({
    initialTab: "inputTab",
    tabs: TABS,
    isEditing: true,
  });
  const { activeTab, changeTab, tabsOrientation } = tabs;

  const {
    isVisible: isNavigateConfirmVisible,
    handleOpen: handleOpenNavigateConfirm,
    handleClose: handleCloseNavigateConfirm,
    selected: stepToNavigate,
  } = useDialog<IWorkflowStep>();

  const resolveStepByName = (stepName: string) =>
    workflowSteps.find((item) => String(item.stepName || "").trim() === stepName.trim());

  const requestNavigateToStep = (targetStep?: IWorkflowStep) => {
    if (!targetStep || !onNavigateToStep) return;
    handleOpenNavigateConfirm(targetStep);
  };

  const confirmNavigateToStep = () => {
    if (!stepToNavigate || !onNavigateToStep) return;
    onNavigateToStep(stepToNavigate);
    handleCloseNavigateConfirm();
  };

  return (
    <>
      <Formik
        key={step?.id ?? "new"}
        initialValues={initialValues}
        validateOnChange={false}
        validationSchema={validationSchema}
        onSubmit={submitForm}
        enableReinitialize
      >
        {({ values, errors, handleChange, submitForm: formikSubmit, setFieldValue }) => {
          const { workflowEngineId } = values;
          const workflowEngines = engines?.items || [];
          const hasEngine = workflowEngineId != null;
          const engParams = hasEngine ? getEngineParams(workflowEngineId, workflowEngines) : [];
          const selectedEngine = workflowEngines.find((engine) => engine.id === workflowEngineId);
          const menuTargetSteps = getMenuOptionTargetSteps(values.menuOption, workflowSteps);
          const gotoTarget = resolveStepByName(values.gotoWorkflowStepName || "");
          const exceptionTarget = resolveStepByName(
            values.exceptionSourceWorkflowStepName || ""
          );

          return (
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormField
                    variant="outlined"
                    name="stepNumber"
                    label={messages.stepNumberLabel}
                    value={values.stepNumber}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    inputProps={{ min: 1 }}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.stepNumber}
                    disabled={!isAdmin}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormField
                    variant="outlined"
                    name="stepName"
                    label={messages.stepNameLabel}
                    value={values.stepName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\s+/g, "");
                      setFieldValue("stepName", value);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.stepName}
                    disabled={!isAdmin}
                    required={!isEditing}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Autocomplete
                    fullWidth
                    options={workflowElementIdOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={
                      workflowElementIdOptions.find(
                        (opt) => opt.value === values.workflowElementId
                      ) || null
                    }
                    disabled={!isAdmin}
                    onChange={(_, newValue) => {
                      if (newValue) setFieldValue("workflowElementId", newValue.value);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        label={messages.workflowElementIdLabel}
                        {...params}
                        variant="outlined"
                        error={!!errors.workflowElementId}
                        helperText={errors.workflowElementId}
                        disabledIcon
                        disabled={!isAdmin}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <Autocomplete
                    fullWidth
                    options={stepTypeOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={stepTypeOptions.find((opt) => opt.value === values.stepType) || null}
                    disabled={!isAdmin}
                    onChange={(_, newValue) => {
                      if (newValue) setFieldValue("stepType", newValue.value);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        label={messages.stepTypeLabel}
                        {...params}
                        variant="outlined"
                        error={!!errors.stepType}
                        helperText={errors.stepType}
                        disabledIcon
                        disabled={!isAdmin}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Autocomplete
                    fullWidth
                    options={allowAttendantTransferOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={
                      values.allowAttendantTransfer === null ||
                      values.allowAttendantTransfer === undefined
                        ? null
                        : allowAttendantTransferOptions.find(
                            (opt) => opt.value === values.allowAttendantTransfer
                          ) || null
                    }
                    disabled={!isAdmin}
                    onChange={(_, newValue) => {
                      setFieldValue("allowAttendantTransfer", newValue ? newValue.value : null);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        label={messages.allowAttendantTransferLabel}
                        {...params}
                        variant="outlined"
                        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                        disabledIcon
                        disabled={!isAdmin}
                      />
                    )}
                  />
                </Grid>


                <Grid size={12}>
                  <MDTypography variant="subtitle2" mb={1}>
                    {messages.globalVariablesLabel}
                  </MDTypography>
                  {isLoadingGlobalVariables ? (
                    <Grid container sx={{ display: "flex", gap: 1 }}>
                      {[...Array(7)].map((_, index) => (
                        <Skeleton key={index} variant="rectangular" width={65} height={32} />
                      ))}
                    </Grid>
                  ) : globalVariables && globalVariables.items.length > 0 ? (
                    <Grid container sx={{ display: "flex", gap: 1 }}>
                      {globalVariables.items.map((item, index) => (
                        <Chip
                          key={index}
                          label={parseParameters(item.name)[0]}
                          onClick={() =>
                            setFieldValue(
                              "messageBody",
                              `${values.messageBody}#${parseParameters(item.name)[0]}#`
                            )
                          }
                        />
                      ))}
                    </Grid>
                  ) : (
                    <MDTypography variant="button" color="text" mt={1}>
                      {messages.noVariablesAvailable}
                    </MDTypography>
                  )}
                  <MDTypography variant="caption" color="text" mt={1} display="block">
                    {messages.globalVariablesHint}
                  </MDTypography>
                </Grid>

                <Grid size={12}>
                  <AppBar position="static" sx={{ borderRadius: 1 }}>
                    <Tabs
                      orientation={tabsOrientation}
                      value={activeTab}
                      onChange={(_, newTab) => changeTab(newTab)}
                    >
                      {TABS.map((tab) => (
                        <Tab key={tab.value} label={tab.label} value={tab.value} />
                      ))}
                    </Tabs>
                  </AppBar>
                </Grid>

                {activeTab === "inputTab" && (
                  <>
                    <Grid size={12}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 7 }}>
                          <PromptAIAutocompleteField
                            label={messages.promptAIInputIdLabel}
                            fieldName="promptAIInputId"
                            value={values.promptAIInputId}
                            setFieldValue={setFieldValue}
                            disabled={!isAdmin}
                            onSelectedOptionChange={setSelectedPromptInput}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <ContentPreviewBubble
                            content={getPromptAIContent(selectedPromptInput)}
                            emptyText={messages.contentPreviewEmpty}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid size={12}>
                      <FormField
                        externalLabel
                        name="startMessage"
                        label={messages.startMessageLabel}
                        value={values.startMessage}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.startMessage}
                        helperText={errors.startMessage}
                      />
                    </Grid>

                    <Grid size={12}>
                      <Grid container spacing={1} alignItems="flex-start">
                        <Grid size="grow">
                          <FormField
                            externalLabel
                            name="menuOption"
                            label={messages.menuOptionLabel}
                            value={values.menuOption || ""}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={12}
                            error={!!errors.menuOption}
                            helperText={errors.menuOption || messages.menuOptionHint}
                            disabled={!isAdmin}
                          />
                        </Grid>
                        {onNavigateToStep && menuTargetSteps.length > 0 && (
                          <Grid
                            size="auto"
                            sx={{ pt: 4, display: "flex", flexDirection: "column", gap: 1 }}
                          >
                            {menuTargetSteps.map((target) => (
                              <StepNavigateShortcutButton
                                key={target.id}
                                ariaLabel={`${messages.stepNavigateShortcutAriaLabel}: ${target.stepName}`}
                                onClick={() => requestNavigateToStep(target)}
                              />
                            ))}
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </>
                )}

                {activeTab === "processingTab" && (
                  <>
                    <Grid size={12}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 7 }}>
                          <Autocomplete
                            fullWidth
                            options={workflowEngineIdOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            value={
                              workflowEngineIdOptions.find(
                                (opt) => opt.value === values.workflowEngineId
                              ) || null
                            }
                            disabled={!isAdmin}
                            onChange={(_, newValue) => {
                              const nextValue =
                                newValue == null || newValue.value === 0 ? null : newValue.value;
                              void setFieldValue("workflowEngineId", nextValue);
                            }}
                            renderInput={(params) => (
                              <MDInput
                                label={messages.workflowEngineIdLabel}
                                {...params}
                                variant="outlined"
                                error={!!errors.workflowEngineId}
                                helperText={errors.workflowEngineId}
                                disabledIcon
                                disabled={!isAdmin}
                              />
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <ContentPreviewBubble
                            content={
                              values.workflowEngineId != null && values.workflowEngineId !== 0
                                ? getEnginePreview(selectedEngine)
                                : ""
                            }
                            emptyText={messages.contentPreviewEmpty}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid size={12}>
                      <MDTypography variant="subtitle2" mb={1}>
                        {messages.engineParametersLabel}
                      </MDTypography>
                      {isLoadingEngParams ? (
                        <Grid container sx={{ display: "flex", gap: 1 }}>
                          {[...Array(3)].map((_, index) => (
                            <Skeleton key={index} variant="rectangular" width={80} height={32} />
                          ))}
                        </Grid>
                      ) : engParams.length > 0 ? (
                        <Grid container sx={{ display: "flex", gap: 1 }}>
                          {engParams.map((param, index) => (
                            <Chip
                              sx={{ cursor: "pointer" }}
                              key={index}
                              label={param}
                              onClick={() =>
                                setFieldValue("messageBody", `${values.messageBody}#${param}#`)
                              }
                            />
                          ))}
                        </Grid>
                      ) : (
                        <MDTypography variant="button" color="text" mt={1}>
                          {messages.noVariablesAvailable}
                        </MDTypography>
                      )}
                      <MDTypography variant="caption" color="text" mt={1} display="block">
                        {messages.engineParametersHint}
                      </MDTypography>
                    </Grid>

                    <Grid size={12}>
                      <FormField
                        externalLabel
                        name="startMessageEngine"
                        label={messages.startMessageEngineLabel}
                        value={values.startMessageEngine}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.startMessageEngine}
                        helperText={errors.startMessageEngine}
                      />
                    </Grid>

                    <Grid size={12}>
                      <FormField
                        externalLabel
                        name="messageBody"
                        label={messages.messageBodyLabel}
                        value={values.messageBody}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={!isAdmin}
                        error={!!errors.messageBody}
                        helperText={errors.messageBody}
                      />
                    </Grid>

                    <Grid size={12}>
                      <FormField
                        externalLabel
                        name="endMessageEngine"
                        label={messages.endMessageEngineLabel}
                        value={values.endMessageEngine}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.endMessageEngine}
                        helperText={errors.endMessageEngine}
                      />
                    </Grid>
                  </>
                )}

                {activeTab === "outputTab" && (
                  <>
                    <Grid size={12}>
                      <StepNameAutocomplete
                        label={messages.gotoWorkflowStepNameLabel}
                        options={gotoStepOptions}
                        value={values.gotoWorkflowStepName || ""}
                        error={errors.gotoWorkflowStepName}
                        disabled={!isAdmin}
                        shortcutAriaLabel={messages.stepNavigateShortcutAriaLabel}
                        onChange={(value) => setFieldValue("gotoWorkflowStepName", value)}
                        onNavigate={
                          onNavigateToStep ? () => requestNavigateToStep(gotoTarget) : undefined
                        }
                        canNavigate={!!gotoTarget}
                      />
                    </Grid>

                    <Grid size={12}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 7 }}>
                          <PromptAIAutocompleteField
                            label={messages.promptAIExceptionIdLabel}
                            fieldName="promptAIExceptionId"
                            value={values.promptAIExceptionId}
                            setFieldValue={setFieldValue}
                            disabled={!isAdmin}
                            onSelectedOptionChange={setSelectedPromptException}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <ContentPreviewBubble
                            content={getPromptAIContent(selectedPromptException)}
                            emptyText={messages.contentPreviewEmpty}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid size={12}>
                      <FormField
                        externalLabel
                        name="exceptionMessage"
                        label={messages.exceptionMessageLabel}
                        value={values.exceptionMessage}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.exceptionMessage}
                        helperText={errors.exceptionMessage}
                      />
                    </Grid>

                    <Grid size={12}>
                      <StepNameAutocomplete
                        label={messages.exceptionSourceWorkflowStepNameLabel}
                        options={exceptionSourceOptions}
                        value={values.exceptionSourceWorkflowStepName || ""}
                        error={errors.exceptionSourceWorkflowStepName}
                        disabled={!isAdmin}
                        shortcutAriaLabel={messages.stepNavigateShortcutAriaLabel}
                        onChange={(value) =>
                          setFieldValue("exceptionSourceWorkflowStepName", value)
                        }
                        onNavigate={
                          onNavigateToStep
                            ? () => requestNavigateToStep(exceptionTarget)
                            : undefined
                        }
                        canNavigate={!!exceptionTarget}
                      />
                    </Grid>
                  </>
                )}

                <Grid size={12}>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    fullWidth
                    isLoading={isLoading}
                    onClick={() => {
                      formikSubmit();
                    }}
                  >
                    {isEditing ? messages.updateButton : messages.createButton}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          );
        }}
      </Formik>

      <ConfirmationDialog
        isOpen={isNavigateConfirmVisible}
        onClose={handleCloseNavigateConfirm}
        onConfirm={confirmNavigateToStep}
        titleText={messages.stepNavigateConfirmTitle}
        descriptionText={messages.stepNavigateConfirmMessage}
        cancelText={messages.cancelButton}
        confirmText={messages.confirmButton}
      />
    </>
  );
};

export default WorkflowStepForm;
